"use client";

import React, { useEffect, useState } from 'react';
import { Search, Plus, Filter, MoreVertical, ExternalLink, Mail, Edit2, Trash2, Save, X, Phone, MapPin, Users } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';

interface Client {
  id: string;
  name: string;
  media_name: string;
  email?: string;
  phone?: string;
  tier: string;
  status: string;
  portal_access: boolean;
  address?: string;
  contact_person?: string;
  created_at: string;
  updated_at: string;
}

const Clients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchClients();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('clients-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'clients' },
        () => fetchClients()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, tierFilter, statusFilter]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    let filtered = clients;

    if (searchTerm) {
      filtered = filtered.filter(client => 
        client.media_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (tierFilter !== 'all') {
      filtered = filtered.filter(client => client.tier === tierFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(client => client.status === statusFilter);
    }

    setFilteredClients(filtered);
  };

  const handleSaveClient = async (client: Client) => {
    try {
      const clientData = {
        name: client.name,
        media_name: client.media_name,
        email: client.email,
        phone: client.phone,
        tier: client.tier,
        status: client.status,
        portal_access: client.portal_access,
        address: client.address,
        contact_person: client.contact_person
      };

      if (editingClient?.id) {
        // Update existing client
        const { error } = await supabase
          .from('clients')
          .update(clientData)
          .eq('id', client.id);

        if (error) throw error;
      } else {
        // Create new client
        const { error } = await supabase
          .from('clients')
          .insert(clientData);

        if (error) throw error;
      }

      setEditingClient(null);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este cliente?')) return;
    
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Grande': return 'bg-purple-50 text-purple-600';
      case 'Mediano': return 'bg-blue-50 text-blue-600';
      case 'Regional': return 'bg-green-50 text-green-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-600';
      case 'paused': return 'bg-yellow-50 text-yellow-600';
      case 'inactive': return 'bg-red-50 text-red-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-charcoal">Clientes</h1>
          <p className="text-charcoal-muted">Gestiona la relación con los medios dominicanos.</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus size={20} /> Nuevo Cliente
        </button>
      </div>

      <div className="bg-white rounded-4xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex flex-col lg:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar por medio, contacto o email..." 
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Todos los tiers</option>
              <option value="Grande">Grande</option>
              <option value="Mediano">Mediano</option>
              <option value="Regional">Regional</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="paused">Pausado</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-charcoal-muted text-xs font-bold uppercase tracking-widest">
              <tr>
                <th className="px-8 py-4">Medio</th>
                <th className="px-8 py-4">Contacto</th>
                <th className="px-8 py-4">Tier</th>
                <th className="px-8 py-4">Estado</th>
                <th className="px-8 py-4">Acceso Portal</th>
                <th className="px-8 py-4">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredClients.length > 0 ? filteredClients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-8 py-6">
                    <div>
                      <p className="font-bold text-charcoal">{client.media_name}</p>
                      <p className="text-sm text-charcoal-muted">{client.name}</p>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <div className="space-y-1">
                      {client.contact_person && (
                        <p className="text-sm font-medium text-charcoal">{client.contact_person}</p>
                      )}
                      {client.email && (
                        <div className="flex items-center gap-1 text-xs text-charcoal-muted">
                          <Mail size={12} />
                          <span>{client.email}</span>
                        </div>
                      )}
                      {client.phone && (
                        <div className="flex items-center gap-1 text-xs text-charcoal-muted">
                          <Phone size={12} />
                          <span>{client.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${getTierColor(client.tier)}`}>
                      {client.tier}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${getStatusColor(client.status)}`}>
                      {client.status === 'active' ? 'Activo' : client.status === 'paused' ? 'Pausado' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                      client.portal_access ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-600'
                    }`}>
                      {client.portal_access ? 'Sí' : 'No'}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setEditingClient(client)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Edit2 size={16} className="text-gray-400" />
                      </button>
                      <button 
                        onClick={() => handleDeleteClient(client.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-8 py-12 text-center text-charcoal-muted">
                    <div className="flex flex-col items-center">
                      <Users size={48} className="mb-4 opacity-20" />
                      <p>No se encontraron clientes</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Client Modal */}
      {(showAddModal || editingClient) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-charcoal">
                {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h2>
              <button 
                onClick={() => {
                  setEditingClient(null);
                  setShowAddModal(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Nombre del Contacto</label>
                <input
                  type="text"
                  value={editingClient?.name || ''}
                  onChange={(e) => setEditingClient(editingClient ? {...editingClient, name: e.target.value} : {
                    id: '',
                    name: e.target.value,
                    media_name: '',
                    tier: 'Mediano',
                    status: 'active',
                    portal_access: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Nombre del Medio</label>
                <input
                  type="text"
                  value={editingClient?.media_name || ''}
                  onChange={(e) => setEditingClient(editingClient ? {...editingClient, media_name: e.target.value} : {
                    id: '',
                    name: '',
                    media_name: e.target.value,
                    tier: 'Mediano',
                    status: 'active',
                    portal_access: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Email</label>
                <input
                  type="email"
                  value={editingClient?.email || ''}
                  onChange={(e) => setEditingClient(editingClient ? {...editingClient, email: e.target.value} : {
                    id: '',
                    name: '',
                    media_name: '',
                    email: e.target.value,
                    tier: 'Mediano',
                    status: 'active',
                    portal_access: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={editingClient?.phone || ''}
                  onChange={(e) => setEditingClient(editingClient ? {...editingClient, phone: e.target.value} : {
                    id: '',
                    name: '',
                    media_name: '',
                    phone: e.target.value,
                    tier: 'Mediano',
                    status: 'active',
                    portal_access: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Tier</label>
                <select
                  value={editingClient?.tier || 'Mediano'}
                  onChange={(e) => setEditingClient(editingClient ? {...editingClient, tier: e.target.value} : {
                    id: '',
                    name: '',
                    media_name: '',
                    tier: e.target.value,
                    status: 'active',
                    portal_access: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="Regional">Regional</option>
                  <option value="Mediano">Mediano</option>
                  <option value="Grande">Grande</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Estado</label>
                <select
                  value={editingClient?.status || 'active'}
                  onChange={(e) => setEditingClient(editingClient ? {...editingClient, status: e.target.value} : {
                    id: '',
                    name: '',
                    media_name: '',
                    tier: 'Mediano',
                    status: e.target.value,
                    portal_access: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="active">Activo</option>
                  <option value="paused">Pausado</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-charcoal mb-2">Dirección</label>
                <input
                  type="text"
                  value={editingClient?.address || ''}
                  onChange={(e) => setEditingClient(editingClient ? {...editingClient, address: e.target.value} : {
                    id: '',
                    name: '',
                    media_name: '',
                    address: e.target.value,
                    tier: 'Mediano',
                    status: 'active',
                    portal_access: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="portal_access"
                checked={editingClient?.portal_access || false}
                onChange={(e) => setEditingClient(editingClient ? {...editingClient, portal_access: e.target.checked} : {
                  id: '',
                  name: '',
                  media_name: '',
                  tier: 'Mediano',
                  status: 'active',
                  portal_access: e.target.checked,
                  created_at: '',
                  updated_at: ''
                })}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label htmlFor="portal_access" className="text-sm font-medium text-charcoal">
                Acceso al portal
              </label>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setEditingClient(null);
                  setShowAddModal(false);
                }}
                className="flex-1 px-6 py-3 border border-gray-200 text-charcoal rounded-2xl font-bold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => editingClient && handleSaveClient(editingClient)}
                className="flex-1 btn-primary"
              >
                <Save size={20} /> {editingClient ? 'Guardar Cambios' : 'Crear Cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;